import React, { useEffect, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    Package,
    Trash2,
    AlertCircle,
    CheckCircle,
    XCircle,
    Truck,
    Send,
    Edit,
    Eye,
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
// import SupplyItem from "./SupplyItem";
import ExportData from "../../components/common/ExportData";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import SupplyItem from "../Authorization/SupplyItem";
import RequisitionItemDetailsModal from "./RequisitionItemDetailsModal";

const DispatchedWarehouseRequisition = () => {
    const navigate = useNavigate();
    const { requestWithAuth } = useAuth();
    const [requisitionItems, setRequisitionItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [removingItem, setRemovingItem] = useState(false);
    const [supplyModalOpen, setSupplyModalOpen] = useState(false);
    const [selectedDrug, setSelectedDrug] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [dispatching, setDispatching] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedDetailsItemId, setSelectedDetailsItemId] = useState(null);

    const fetchItemsList = async (page_size, search_query, current_page) => {
        setLoading(true);
        try {
            const data = await requestWithAuth(
                `/inventory/stores/warehouse-requisition/?status=Dispatched&page=${current_page}&pageSize=${page_size}&search=${search_query}`,
                "GET"
            );
            setRequisitionItems(data?.results);
            setNextPage(data?.next);
            setPrevPage(data?.prev);
            setTotalPages(Math.ceil(data?.count / pageSize));
        } catch (error) {
            console.error("Error fetching invoice data:", error);
        } finally {
            setLoading(false);
        }
    };

    const getTableData = async (page_size, search_query, current_page) => {
        setLoading(true);
        try {
            const data = await requestWithAuth(
                `/inventory/stores/warehouse-requisition/?status=Dispatched&paginated=false&search=${search_query}`,
                "GET"
            );
            let inventoryData = [];

            for (let i = 0; i <= data?.length - 1; i++) {
                inventoryData.push({
                    ID: data[i]?.id,
                    Drug: data[i]?.drug,
                    "Quantity Requested": data[i]?.quantity,
                    "Current Stock": data[i]?.current_stock,
                    Branch: data[i]?.branch?.name,
                    "Date Requested": data[i]?.created_at ? new Date(data[i].created_at).toLocaleString() : "N/A",
                    "Rejection Reason": data[i]?.rejection_reason,
                });
            }
            setTableData(inventoryData);
        } catch (error) {
            console.error("Error fetching invoice data:", error);
        } finally {
            setLoading(false);
        }
    };

    const removeItem = async (item_id) => {
        Swal.fire({
            icon: "question",
            title: "Are you sure you want to delete this item?",
            showDenyButton: true,
            confirmButtonText: "Yes Proceed!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setRemovingItem(true);
                    const data = await requestWithAuth(
                        `/inventory/requisition/branch/${item_id}/item`,
                        "DELETE"
                    );
                    Swal.fire({
                        icon: "success",
                        title: "Requested!",
                        text: "Item Removed Successfully",
                    }).then(() => {
                        window.location.reload();
                    });
                } catch (error) {
                    toast.error("Something went wrong!");
                } finally {
                    setRemovingItem(false);
                }
            } else if (result.isDenied) {
                Swal.fire("Changes are not saved", "", "info");
            }
        });
    };

    const handleSelectItem = (item) => {
        if (item?.status !== "Supplied") {
            toast.warning("You can only select items with 'Supplied' status");
            return;
        }

        setSelectedItems((prev) => {
            const isSelected = prev.find((i) => i.id === item.id);
            if (isSelected) {
                return prev.filter((i) => i.id !== item.id);
            } else {
                return [...prev, item];
            }
        });
    };

    const handleSelectAll = () => {
        const suppliedItems = requisitionItems.filter(
            (item) => item?.status === "Supplied"
        );
        if (selectedItems.length === suppliedItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(suppliedItems);
        }
    };

    const handleDispatch = async () => {
        if (selectedItems.length === 0) {
            toast.warning("Please select at least one item to dispatch");
            return;
        }

        Swal.fire({
            icon: "question",
            title: `Dispatch ${selectedItems.length} item(s)?`,
            text: "Are you sure you want to dispatch the selected items?",
            showDenyButton: true,
            confirmButtonText: "Yes, Dispatch!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setDispatching(true);

                    // Create shipment data
                    const shipmentData = {
                        items: selectedItems.map((item) => item.id),
                        branch_name: requisitionItems[0]?.branch?.name,
                        branch_id: requisitionItems[0]?.branch?.id,
                    };

                    // Store in sessionStorage to pass to dispatch page
                    sessionStorage.setItem(
                        "dispatchData",
                        JSON.stringify({
                            shipmentData,
                            selectedItems,
                            branchName: requisitionItems[0]?.branch?.name,
                        })
                    );

                    // Navigate to dispatch page
                    navigate(
                        `/authorization/requisition/dispatch/${requisitionItems[0]?.id}`
                    );
                } catch (error) {
                    toast.error("Something went wrong!");
                    console.error(error);
                } finally {
                    setDispatching(false);
                }
            }
        });
    };

    const handleSupplyClick = (item) => {
        setSelectedDrug(item);
        setIsEditMode(false);
        setSupplyModalOpen(true);
    };

    const handleEditSupplyClick = (item) => {
        setSelectedDrug(item);
        setIsEditMode(true);
        setSupplyModalOpen(true);
    };

    // Determine what actions are available based on status
    const getAvailableActions = (status) => {
        switch (status) {
            case "Requested":
                return {
                    canSupply: true,
                    canEdit: false,
                    canRemove: true,
                };
            case "Supplied":
                return {
                    canSupply: false,
                    canEdit: false,
                    canRemove: false,
                };
            case "Dispatched":
                return {
                    canSupply: false,
                    canEdit: false,
                    canRemove: false,
                };
            case "Rejected":
                return {
                    canSupply: true,
                    canEdit: false,
                    canRemove: true,
                };
            default:
                return {
                    canSupply: false,
                    canEdit: false,
                    canRemove: false,
                };
        }
    };

    const filterItemsList = (search_query) => {
        setSearch(search_query);
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        if (nextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (prevPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const totalNumbersToShow = 3;

        let startPage = Math.max(1, currentPage - totalNumbersToShow);
        let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) {
                pageNumbers.push("...");
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push("...");
            }
            pageNumbers.push(totalPages);
        }

        return pageNumbers.map((number, idx) => (
            <button
                key={idx}
                onClick={() => number !== "..." && handlePageClick(number)}
                disabled={number === "..."}
                className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
                        ? "bg-blue-500 text-white shadow-sm"
                        : number === "..."
                            ? "cursor-default text-gray-400"
                            : "text-gray-600 hover:bg-gray-100"
                    }`}
            >
                {number}
            </button>
        ));
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            Requested: { color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
            Supplied: { color: "bg-blue-100 text-blue-800", icon: Truck },
            Dispatched: { color: "bg-purple-100 text-purple-800", icon: Send },
            Approved: { color: "bg-green-100 text-green-800", icon: CheckCircle },
            Rejected: { color: "bg-red-100 text-red-800", icon: XCircle },
        };

        const config = statusConfig[status] || {
            color: "bg-gray-100 text-gray-800",
            icon: AlertCircle,
        };
        const Icon = config.icon;

        return (
            <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
            >
                <Icon size={14} />
                {status}
            </span>
        );
    };

    const suppliedItemsCount = requisitionItems.filter(
        (item) => item?.status === "Supplied"
    ).length;

    useEffect(() => {
        fetchItemsList(pageSize, search, currentPage);
        getTableData(pageSize, search, currentPage);
    }, [search, currentPage, pageSize]);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {supplyModalOpen && (
                <SupplyItem
                    modalOpen={supplyModalOpen}
                    setModalOpen={setSupplyModalOpen}
                    item={selectedDrug}
                    isEditMode={isEditMode}
                />
            )}
            <RequisitionItemDetailsModal
                modalOpen={detailsModalOpen}
                setModalOpen={setDetailsModalOpen}
                itemId={selectedDetailsItemId}
            />

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <span>Inventory</span>
                                <span>/</span>
                                <Link
                                    to="/authorization/requisition"
                                    className="hover:text-gray-700"
                                >
                                    Authorization
                                </Link>
                                <span>/</span>
                                <span className="text-gray-900 font-medium">
                                    Requisition Details
                                </span>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Dispatched Warehouse Requisition
                            </h1>
                        </div>

                        {/* Dispatch Button */}
                        {selectedItems.length > 0 && (
                            <button
                                onClick={handleDispatch}
                                disabled={dispatching}
                                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {dispatching ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Dispatching...
                                    </>
                                ) : (
                                    <>
                                        <Send size={20} />
                                        Dispatch ({selectedItems.length})
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Selection Info */}
                    {suppliedItemsCount > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                                <strong>{selectedItems.length}</strong> of{" "}
                                <strong>{suppliedItemsCount}</strong> supplied items selected
                                for dispatch
                            </p>
                        </div>
                    )}
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    {/* Filters Bar */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-3">
                                <ExportData
                                    tableData={tableData}
                                    fileName={`Branch Requisition ${requisitionItems[0]?.branch?.name}`}
                                />
                                <span className="text-sm text-gray-600">Show</span>
                                <select
                                    onChange={(e) => setPageSize(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                    value={pageSize}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span className="text-sm text-gray-600">entries</span>
                            </div>

                            <DebouncedSearchInput
                                onSearch={filterItemsList}
                                placeholder="Search items..."
                                delay={500}
                                className="flex-1 max-w-md"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="flex items-center gap-3 text-gray-600">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                    <span>Loading requisition items...</span>
                                </div>
                            </div>
                        ) : requisitionItems?.length > 0 ? (
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectedItems.length === suppliedItemsCount &&
                                                    suppliedItemsCount > 0
                                                }
                                                onChange={handleSelectAll}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                                                disabled={suppliedItemsCount === 0}
                                            />
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Item Name
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Qty Requested
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Current Stock
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Qty Supplied
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Requisition Date
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Rejection Reason
                                        </th>
                                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {requisitionItems?.map((item, index) => {
                                        const isSelected = selectedItems.find(
                                            (i) => i.id === item.id
                                        );
                                        const canSelect = item?.status === "Supplied";
                                        const actions = getAvailableActions(item?.status);

                                        return (
                                            <tr
                                                key={index}
                                                className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""
                                                    }`}
                                            >
                                                <td className="py-4 px-6">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!isSelected}
                                                        onChange={() => handleSelectItem(item)}
                                                        disabled={!canSelect}
                                                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 ${canSelect
                                                            ? "cursor-pointer"
                                                            : "cursor-not-allowed opacity-50"
                                                            }`}
                                                    />
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-900">
                                                    {item.id}
                                                </td>
                                                <td className="py-4 px-6 text-sm font-medium text-gray-900">
                                                    {item?.drug}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600">
                                                    {item?.quantity}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600">
                                                    {item?.current_stock}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600">
                                                    {item?.quantity_supplied ? (
                                                        <span className="font-medium text-gray-900">
                                                            {item?.quantity_supplied}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 italic text-xs">
                                                            Not supplied
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600">
                                                    {item?.created_at ? new Date(item.created_at).toLocaleString() : "N/A"}
                                                </td>
                                                <td className="py-4 px-6">
                                                    {getStatusBadge(item?.status)}
                                                </td>
                                                <td className="py-4 px-6 text-sm text-gray-600">
                                                    {item?.rejection_reason ? (
                                                        <span className="text-red-600">
                                                            {item?.rejection_reason}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 italic text-xs">
                                                            N/A
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6">
                                                    {removingItem ? (
                                                        <div className="flex items-center gap-2 text-gray-500">
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                                            <span className="text-xs">Processing...</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            {/* View Details Button */}
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedDetailsItemId(item?.id);
                                                                    setDetailsModalOpen(true);
                                                                }}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                            >
                                                                <Eye size={14} />
                                                                View
                                                            </button>

                                                            {/* Supply Button - only for Requested or Rejected */}
                                                            {actions.canSupply && (
                                                                <button
                                                                    onClick={() => handleSupplyClick(item)}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                                >
                                                                    <Truck size={14} />
                                                                    Supply
                                                                </button>
                                                            )}

                                                            {/* Edit Supply Button - only for Supplied */}
                                                            {actions.canEdit && (
                                                                <button
                                                                    onClick={() => handleEditSupplyClick(item)}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                                                >
                                                                    <Edit size={14} />
                                                                    Edit Supply
                                                                </button>
                                                            )}

                                                            {/* Remove Button - only for Requested or Rejected */}
                                                            {actions.canRemove && (
                                                                <button
                                                                    onClick={() => removeItem(item?.id)}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                                                >
                                                                    <Trash2 size={14} />
                                                                    Remove
                                                                </button>
                                                            )}

                                                            {/* No actions available for Dispatched */}
                                                            {!actions.canSupply &&
                                                                !actions.canEdit &&
                                                                !actions.canRemove && (
                                                                    <span className="text-xs text-gray-400 italic">
                                                                        No actions
                                                                    </span>
                                                                )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="py-20 text-center">
                                <div className="text-gray-400 mb-2">
                                    <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                                </div>
                                <p className="text-gray-500 text-sm">
                                    No requisition items found
                                </p>
                                {search && (
                                    <p className="text-gray-400 text-xs mt-1">
                                        Try adjusting your search terms
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {requisitionItems?.length > 0 && (
                        <div className="p-6 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    Showing page {currentPage} of {totalPages}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1}
                                        className={`p-2 rounded-lg transition-all ${currentPage > 1
                                            ? "text-gray-600 hover:bg-gray-100"
                                            : "text-gray-300 cursor-not-allowed"
                                            }`}
                                    >
                                        <ChevronLeft size={20} />
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {renderPageNumbers()}
                                    </div>

                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                                            ? "text-gray-600 hover:bg-gray-100"
                                            : "text-gray-300 cursor-not-allowed"
                                            }`}
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DispatchedWarehouseRequisition;
